import { useState } from "react";
import { Button } from "@material-tailwind/react";
import { format, parse, setWeek, setYear } from "date-fns";
import { api } from "../../../services/api/api";
import PeriodSelect, { PeriodSelectData } from "../date/PeriodSelect";
import { t } from "i18next";
import { buildUnavailabilitySheet, TechnicianUnavailabilityExport, unavailabilityExportLabels } from "./unavailabilityExport";
import * as XLSX from "xlsx";

/**
* TechnicianUnavailabilitiesDownloader: Component to download technicians unavailabilities
*/
export default function TechnicianUnavailabilitiesDownloader() {
  const [isLoading, setIsLoading] = useState(false);
  const [periodData, setPeriodData] = useState<PeriodSelectData>(() => {
    return {
      period: "day",
      value: format(new Date(), "yyyy-MM-dd")
    };
  });

  const getDateRange = () => {
    const today = new Date();
    let from: Date;

    switch (periodData.period) {
    case "week": {
      const [year, weekNumberStr] = periodData.value.split("-W");
      const week = parseInt(weekNumberStr);
      from = new Date();
      from = setYear(from, parseInt(year));
      from = setWeek(from, week, { weekStartsOn: 1 });
      today.setTime(from.getTime());
      today.setDate(today.getDate() + 6);
      break;
    }
    case "month": {
      const [year, month] = periodData.value.split("-");
      from = new Date(parseInt(year), parseInt(month) - 1, 1);
      const lastDayOfMonth = new Date(parseInt(year), parseInt(month), 0);
      today.setFullYear(parseInt(year));
      today.setMonth(parseInt(month) - 1);
      today.setDate(lastDayOfMonth.getDate());
      break;
    }
    default: {
      from = parse(periodData.value, "yyyy-MM-dd", new Date());
      today.setTime(from.getTime());
    }
    }

    return {
      from: format(from, "yyyy-MM-dd"),
      to: format(today, "yyyy-MM-dd")
    };
  };

  const downloadUnavailabilities = async () => {
    setIsLoading(true);
    try {
      const { from, to } = getDateRange();

      const response = await api.get(`admin/unavail?from=${from}&to=${to}`);
      const labels = unavailabilityExportLabels();
      const data = buildUnavailabilitySheet(response as TechnicianUnavailabilityExport[], labels);

      // Create workbook with one worksheet
      const workbook = XLSX.utils.book_new();
      
      // Add worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(workbook, worksheet, labels.sheet);
      
      // Generate Excel file
      XLSX.writeFile(workbook, `unavailabilities_${from}_${to}.xlsx`);
    } catch (error) {
      console.error("Download error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full p-4">
      <div className="flex-grow pb-4">
        <PeriodSelect 
          onChange={setPeriodData}
          initialData={{
            period: "day",
            value: format(new Date(), "yyyy-MM-dd")
          }}
        />
      </div>

      <div className="footer mt-auto pt-4">
        <Button 
          onClick={downloadUnavailabilities}
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 bg-blue-500" 
          color="blue"
          size="sm"
        >
          {isLoading ? t("common:downloading") : t("technician:exportUnavailability")}
        </Button>
      </div>
    </div>
  );
} 